import { useCallback } from 'react';
import { ComponentFill, ComponentSize, Container, Orientation, Typography, TypographyVariant, ColorDot, DensitySpacing, ComponentPadding } from '@tcc/fds';
import { RegionReportItemsValue } from './RegionReportItemsValue';
import { LabelColor, YieldRegionReport, isBySeedProductItem, ColorsMap, RgbColor, isYieldRegionReport, ApplicationStats, BySeedProductDimension } from '../../../../types';
import type { RootLevelDetails } from '../container/types/modals';
import { getDisplayItem } from './displayFormatting';
import { SetItemCallback } from '../container/useRegionReportController';

export type ColorStatsLabelProps = {
  colors: LabelColor | ColorsMap;
  dataTestId: string;
  item: YieldRegionReport | ApplicationStats | BySeedProductDimension;
  onClickItem?: SetItemCallback;
  index: number;
  cropCode: string;
  cropNominalWeight?: number;
};

function getCircleColor(item: RootLevelDetails, colorsList: LabelColor | ColorsMap): string {
  if(!isYieldRegionReport(item) && isBySeedProductItem(item) && typeof colorsList === 'object') {
    let seedProductColor: RgbColor = { red: 0, green: 0, blue: 0, alpha: 1 };
    // Lookup by product id
    const seedProductIdKey = item.seedProduct.id
    if(seedProductIdKey in colorsList) {
      seedProductColor = colorsList[seedProductIdKey];
    }

    // Lookup by product name
    const seedProductNameKey = item.seedProduct.name ?? ''
    if(seedProductNameKey in colorsList) {
      seedProductColor = colorsList[seedProductNameKey];
    }

    return `rgba(${seedProductColor.red}, ${seedProductColor.green}, ${seedProductColor.blue}, ${seedProductColor.alpha})`;
  }

  const legacyColorList: LabelColor = colorsList as LabelColor ?? [];
  const legacyItem = item as YieldRegionReport;
  const matches = legacyColorList.filter((color) => color.label === legacyItem.label);
  return matches.length > 0 ? matches[0].color : '';
}

export const ColorStatsLabel = ({
  colors,
  dataTestId,
  index,
  item,
  cropCode,
  cropNominalWeight,
  onClickItem,
}: ColorStatsLabelProps) => {
  const { averageYield, area, label } = getDisplayItem(item);
  const openDetails = useCallback(() => {
    onClickItem?.(item);
  }, [item, onClickItem]);

  const id = `dmb-region-report-${dataTestId}-yield-stats-label`
  const isArdData = !isYieldRegionReport(item);

  return (
    <Container
      testId={id}
      key={`dmb-region-report-item-${label}`}
      width='100%'
      orientation={Orientation.Horizontal}
      padding={DensitySpacing.Related}
      gap={DensitySpacing.None}
      >
      <Container
        orientation={Orientation.Horizontal}
        width='50%'
        padding={ComponentPadding.None}
      >
        <ColorDot
          backgroundColor={getCircleColor(item, colors) }
          size={ComponentSize.Md}
          fill={ComponentFill.Clean}
          border={false}
          testId="hybrid-circle"
          />
        <Typography variant={TypographyVariant.Label} testId={`dmb-region-report-${dataTestId}-label`}>
          {label}
        </Typography>
      </Container>

      <RegionReportItemsValue 
        area={area} 
        averageYield={averageYield} 
        dataTestId={dataTestId} 
        index={index}
        cropCode={cropCode}
        cropNominalWeight={cropNominalWeight}
        onClick={openDetails}
        isArdData={isArdData}
      />
    </Container>
  );
};